import { message } from 'antd';
import type { AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import type { ActionType } from 'typesafe-actions';

import { client } from '@portal/api-client/src/client';
import type { FeatureFlag } from '@portal/api-models';
import { getPortalError } from '@portal/common';

import {
  createFeatureFlagAction,
  deleteFeatureFlagAction,
  disableFeatureFlagAction,
  enableFeatureFlagAction,
  getAllFeatureFlagsAction,
  toggleCreate,
  updateFeatureFlagAction,
} from './actions';

export function* getAllFeatureFlags() {
  try {
    const { data: response }: AxiosResponse<FeatureFlag[]> = yield call(
      [client, 'get'],
      '/feature-flags/',
      {
        params: { all: true, limit: 1000 },
      }
    );

    yield put(getAllFeatureFlagsAction.success(response));
  } catch (err: any) {
    yield put(getAllFeatureFlagsAction.failure(err));
  }
}

export function* createFeatureFlag({
  payload,
}: ActionType<typeof createFeatureFlagAction.request>) {
  try {
    const { data: response }: AxiosResponse<FeatureFlag> = yield call(
      [client, 'post'],
      '/feature-flags/',
      payload
    );

    yield put(createFeatureFlagAction.success(response));

    yield call([message, 'success'], 'Feature flag created successfully');
    yield put(toggleCreate());
  } catch (err: any) {
    const portalError = getPortalError(err);

    yield put(createFeatureFlagAction.failure(portalError));
  }
}

function* updateFeatureFlag({
  payload,
}: ActionType<typeof updateFeatureFlagAction.request>) {
  try {
    const { data: response }: AxiosResponse<FeatureFlag> = yield call(
      [client, 'put'],
      `/feature-flags/${payload.uuid}`,
      payload.updateData
    );

    yield put(updateFeatureFlagAction.success(response));
    yield call([message, 'success'], 'Feature flag updated successfully');
  } catch (err: any) {
    yield put(updateFeatureFlagAction.failure(err));
  }
}

export function* enableFeatureFlag({
  payload,
}: ActionType<typeof enableFeatureFlagAction.request>) {
  try {
    let url = `/feature-flags/${payload.uuid}`;
    let body;

    if (payload.isStaging) {
      body = {
        enabledStaging: true,
      };
    } else {
      url += '/enable';
    }

    const { data: response }: AxiosResponse<FeatureFlag> = yield call(
      [client, 'put'],
      url,
      body
    );

    yield put(enableFeatureFlagAction.success(response));
    yield call([message, 'success'], 'Feature flag updated successfully');
  } catch (err: any) {
    yield put(enableFeatureFlagAction.failure(err));
  }
}

export function* disableFeatureFlag({
  payload,
}: ActionType<typeof disableFeatureFlagAction.request>) {
  try {
    let url = `/feature-flags/${payload.uuid}`;
    let body;

    if (payload.isStaging) {
      body = {
        enabledStaging: false,
      };
    } else {
      url += '/disable';
    }

    const { data: response }: AxiosResponse<FeatureFlag> = yield call(
      [client, 'put'],
      url,
      body
    );

    yield put(disableFeatureFlagAction.success(response));
    yield call([message, 'success'], 'Feature flag disabled successfully');
  } catch (err: any) {
    yield put(disableFeatureFlagAction.failure(err));
  }
}

export function* deleteFeatureFlag({
  payload,
}: ActionType<typeof deleteFeatureFlagAction.request>) {
  try {
    yield call([client, 'delete'], `/feature-flags/${payload.uuid}`);
    yield put(deleteFeatureFlagAction.success({ uuid: payload.uuid }));
    yield call([message, 'success'], 'Feature flag deleted successfully');
  } catch (err: any) {
    yield put(deleteFeatureFlagAction.failure(err));
  }
}

export function* featureFlagSaga() {
  yield takeLatest(
    getType(getAllFeatureFlagsAction.request),
    getAllFeatureFlags
  );
  yield takeLatest(getType(createFeatureFlagAction.request), createFeatureFlag);
  yield takeLatest(getType(updateFeatureFlagAction.request), updateFeatureFlag);
  yield takeLatest(getType(enableFeatureFlagAction.request), enableFeatureFlag);
  yield takeLatest(
    getType(disableFeatureFlagAction.request),
    disableFeatureFlag
  );
  yield takeLatest(getType(deleteFeatureFlagAction.request), deleteFeatureFlag);
}
